import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, Loader2, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AdminLogin() {
  const nav = useNavigate();
  const { loginWithToken } = useAuth();
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!password) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/admin-access", { password });
      await loginWithToken(data.token, data.user);
      if (!data.user?.is_admin) { setErr("Unable to continue. Please check your credentials."); setBusy(false); return; }
      nav("/admin/command-center");
    } catch (e) { setErr(apiError(e)); setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden bg-[#120507]" data-testid="admin-login-page">
      {/* premium background layers */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_#3B0A12_0%,_#120507_55%,_#0B0304_100%)]" />
      <div className="absolute -top-32 -right-24 w-96 h-96 rounded-full bg-[#781826]/25 blur-3xl" />
      <div className="absolute -bottom-40 -left-20 w-96 h-96 rounded-full bg-[#C59B27]/10 blur-3xl" />
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "linear-gradient(#C59B27 1px, transparent 1px), linear-gradient(90deg, #C59B27 1px, transparent 1px)", backgroundSize: "44px 44px" }} />

      <div className="relative w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="font-serif font-bold text-3xl text-[#FAF5F0] tracking-tight">Mana<span className="text-[#C59B27]">Jodi</span></div>
          <p className="text-[11px] uppercase tracking-[0.35em] text-[#B99A5B] mt-2">Secure Access</p>
        </div>

        <div className="rounded-2xl border border-[#C59B27]/20 bg-[#1A090D]/80 backdrop-blur-xl p-8 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.7)]">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#781826] to-[#3B0A12] border border-[#C59B27]/30 flex items-center justify-center text-[#E2C382] mb-6 mx-auto">
            <ShieldCheck className="w-7 h-7" />
          </div>
          <h1 className="text-center font-serif text-xl text-[#FAF5F0] mb-1">Restricted access</h1>
          <p className="text-center text-xs text-[#9C8388] mb-6">Enter your access password to continue.</p>

          {err && <div className="mb-4 text-xs text-[#FCA5A5] bg-[#450A0A]/60 border border-[#7F1D1D] rounded-xl px-4 py-2.5 text-center" data-testid="admin-login-error">{err}</div>}

          <label className="text-[11px] font-medium text-[#B9A0A4] mb-1.5 block">Password</label>
          <div className="relative">
            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7A5F63]" />
            <input data-testid="admin-password" type={show ? "text" : "password"} autoFocus
              className="w-full rounded-xl bg-[#0D0405] border border-[#781826]/40 text-[#FAF5F0] pl-10 pr-10 py-3 outline-none focus:border-[#C59B27] transition-colors"
              placeholder="••••••••••" value={password}
              onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} />
            <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7A5F63] hover:text-[#C59B27]" data-testid="admin-password-toggle">
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          <button data-testid="admin-login-submit" onClick={submit} disabled={busy || !password}
            className="mt-6 w-full py-3 rounded-xl font-semibold bg-gradient-to-r from-[#C59B27] to-[#A67C1F] text-[#20120A] hover:brightness-105 transition disabled:opacity-50 flex items-center justify-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Continue"}
          </button>

          <p className="text-center text-[10px] text-[#6E5559] mt-5 flex items-center justify-center gap-1"><Lock className="w-3 h-3" /> Protected access</p>
        </div>

        <p className="text-center text-xs text-[#6E5559] mt-6"><Link to="/" className="hover:text-[#9E7B1B]">← Back to ManaJodi</Link></p>
      </div>
    </div>
  );
}
